import { Component, Element, forceUpdate, h, Host, Listen, Prop } from "@stencil/core";

import { hasSlot, setPropsIfNull } from "../../../utils/utils";

/**
 * @slot left - Slot for left column content
 * @slot right - Slot for right column content
 */
@Component({
  tag: "stzh-textandimage",
  styleUrl: "stzh-textandimage.scss",
  shadow: true,
})
export class StzhTextandimage {
  /** Size variant */
  @Prop({ reflect: true }) size: "default" | "small" | "large" = "default";

  /** Image position */
  @Prop({ reflect: true }) imagePosition: "left" | "right" = "right";
  /**Image moves underneath text depending on length of text */
  @Prop({ reflect: true }) imageMoves: boolean = false;

  @Element() element: HTMLStzhTextandimageElement;

  private observer: MutationObserver;

  private init = () => {
    const ratios = this.element.querySelectorAll(
      'stzh-ratio[slot="right"], [slot="right"] stzh-ratio, stzh-gallery[slot="right"], [slot="right"] stzh-gallery',
    );

    const galleries = this.element.querySelectorAll('stzh-gallery[slot="right"], [slot="right"] stzh-gallery');

    const leftSlotUsed = hasSlot(this.element, "left");
    const rightSlotUsed = hasSlot(this.element, "right");

    if (this.size === "large" || !(leftSlotUsed && rightSlotUsed)) {
      ratios.forEach(button => {
        setPropsIfNull(button, {
          ratio: "3:2",
          ratioSmall: "16:9",
        } as HTMLStzhRatioElement);
      });
    }

    galleries.forEach(button => {
      setPropsIfNull(button, {
        variant: "single",
      } as HTMLStzhGalleryElement);
    });
    this.checkLineCount();
  };
  private checkLineCount = () => {
    const texts = this.element.querySelectorAll('[slot="left"] stzh-vspace stzh-richtext div');
    if (this.imageMoves) {
      if (texts.length > 0) {
        const lh = getComputedStyle(texts[0]);
        const isShort = texts[0].clientHeight / parseFloat(lh.lineHeight) <= 4;
        this.element.toggleAttribute("short-text", isShort);
      }
    }
  };

  @Listen("resize", { target: "window" })
  onWindowResize() {
    this.checkLineCount();
  }

  connectedCallback() {
    this.init();

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render() {
    const leftSlotUsed = hasSlot(this.element, "left");
    const rightSlotUsed = hasSlot(this.element, "right");

    const isSingle = !(leftSlotUsed && rightSlotUsed);

    const classes = {
      "stzh-textandimage": true,
      "stzh-textandimage--is-single-left": isSingle && leftSlotUsed,
      "stzh-textandimage--is-single-right": isSingle && rightSlotUsed,
      "stzh-textandimage--has-left": leftSlotUsed,
      "stzh-textandimage--has-right": rightSlotUsed,
      [`stzh-textandimage--size-${this.size}`]: !!this.size,
      [`stzh-textandimage--image-position-${this.imagePosition}`]: !!this.imagePosition,
    };

    return (
      <Host single={isSingle} image-position={this.imagePosition}>
        <div class={classes}>
          <div class="stzh-textandimage__left">
            <slot name="left" onSlotchange={() => forceUpdate(this.element)}></slot>
          </div>

          <div class="stzh-textandimage__right">
            <div class={this.imageMoves ? "stzh-textandimage__wrapper" : ""}>
              <slot name="right" onSlotchange={() => forceUpdate(this.element)}></slot>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
